<script setup lang="ts">
/**
 * Duur in uren:minuten ("8:30"), bewaard als seconden. Opvolger van
 * BaseComponents/VTimeInput, nu op VFormField: krijgt daardoor ook een label en toont
 * validatiefouten (de oude versie had een `showErrors`-prop die niets deed).
 */
import { isSilentFocus } from '@/Composables/useAutofocusFirstField'
import { computed, ref, watch } from 'vue'
import VDisplayValue from './VDisplayValue.vue'
import VFormField from './VFormField.vue'
import type { FormFieldBaseProps } from './types'
import { controlClasses, useFieldErrors, useFormFieldBindings, useSplitAttrs } from './useFormField'

defineOptions({ inheritAttrs: false })

const modelValue = defineModel<number | null>()

const props = withDefaults(
    defineProps<
        FormFieldBaseProps & {
            placeholder?: string
            displayOnly?: boolean
        }
    >(),
    {
        placeholder: 'HH:MM',
        displayOnly: false,
        showErrors: true,
    }
)

const emit = defineEmits<{
    (e: 'change'): void
}>()

const { rootAttrs, controlAttrs } = useSplitAttrs()
const errors = useFieldErrors(props)
const fieldBindings = useFormFieldBindings(props, errors)

const inputRef = ref<HTMLInputElement | null>(null)

function focus(): void {
    inputRef.value?.focus()
}

defineExpose({ inputRef, focus })

/** Seconden → "H:MM". Ondersteunt 100+ uur (540000 → "150:00"). */
function secondsToHHMM(totalSeconds: number): string {
    if (!totalSeconds || totalSeconds <= 0) {
        return '0:00'
    }
    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.round((totalSeconds % 3600) / 60)
    return `${hours}:${String(minutes).padStart(2, '0')}`
}

/**
 * "H:MM" → seconden. Aanvaardt "8:30", "830", "12" en "0:45": zonder dubbelpunt zijn
 * de laatste twee cijfers minuten, behalve bij één of twee cijfers (dan uren).
 */
function hhmmToSeconds(input: string): number {
    if (!input || input.trim() === '') {
        return 0
    }

    const cleaned = input.replace(/[^\d:]/g, '')
    if (!cleaned) {
        return 0
    }

    let hours = 0
    let minutes = 0

    if (cleaned.includes(':')) {
        const parts = cleaned.split(':')
        hours = parseInt(parts[0]) || 0
        minutes = parseInt(parts[1]) || 0
    } else if (cleaned.length <= 2) {
        hours = parseInt(cleaned) || 0
    } else {
        hours = parseInt(cleaned.slice(0, -2)) || 0
        minutes = parseInt(cleaned.slice(-2)) || 0
    }

    if (minutes > 59) {
        minutes = 59
    }

    return hours * 3600 + minutes * 60
}

// Tekst die de gebruiker ziet en bewerkt; het model wordt pas bij blur/Enter gezet.
const displayText = ref('')

watch(
    () => modelValue.value,
    (newValue) => {
        const newDisplay = secondsToHHMM(newValue ?? 0)
        // Enkel bijwerken als het verschilt, zodat typen niet onderbroken wordt.
        if (displayText.value !== newDisplay) {
            displayText.value = newDisplay
        }
    },
    { immediate: true }
)

function formatAndEmit(): void {
    const seconds = hhmmToSeconds(displayText.value)
    modelValue.value = seconds
    displayText.value = secondsToHHMM(seconds)
    emit('change')
}

/** Enkel cijfers en dubbelpunt toelaten, ook tijdens het typen. */
function handleInput(event: Event): void {
    const input = event.target as HTMLInputElement
    const filtered = input.value.replace(/[^\d:]/g, '')
    if (filtered !== input.value) {
        displayText.value = filtered
    }
}

/**
 * Bij focus alles selecteren, zodat typen de duur vervangt. Niet bij de autofocus van
 * een modal: dan zou de eerste toets van wie gewoon verder tabt/typt de bestaande
 * waarde wissen (GH #722 review).
 */
function selectAll(event: FocusEvent): void {
    if (isSilentFocus(event)) {
        return
    }
    inputRef.value?.select()
}

const classes = computed<string>(() =>
    controlClasses({
        inputClasses: props.inputClasses,
        invalid: props.invalid,
        hasPrefix: false,
        hasSuffix: true,
    })
)
</script>

<template>
    <VDisplayValue
        v-if="displayOnly"
        v-bind="rootAttrs"
        type="time"
        :value="secondsToHHMM(modelValue ?? 0)"
        :layout="layout"
        :align-right="inputClasses?.includes('text-right') ?? false"
    />

    <VFormField v-else v-bind="{ ...fieldBindings, ...rootAttrs }">
        <!-- controlAttrs bewust als laatste: dan draaien onze eigen handlers eerst. Een
             `@keydown.enter` van de parent (bv. BasicLine: nieuwe regel toevoegen) ziet zo
             al de geformatteerde duur, net als toen die listener via bubbling op de
             wrapper zat. -->
        <input
            ref="inputRef"
            v-model="displayText"
            type="text"
            :id="id ?? name ?? undefined"
            :name="name ?? undefined"
            :placeholder="placeholder"
            :class="classes"
            :disabled="disabled"
            :required="required"
            autocomplete="off"
            @input="handleInput"
            @blur="formatAndEmit"
            @keydown.enter="formatAndEmit"
            @focus="selectAll"
            v-bind="controlAttrs"
        />

        <template #suffix>
            {{ $t('global.hour_short') }}
        </template>
    </VFormField>
</template>
